// Paired foreground clocks. Suspended intervals are reported as gaps, not attention.
export const SPORTSBOOK_TIME_FLUSH_MS = 30_000;
export const SPORTSBOOK_TIME_SAMPLE_MS = 1_000;
export type TimeState = { account: string | null; visible: boolean; widget: boolean; mounted?: boolean };
export type TimeObservation = {
  time_tracking_version: 1;
  time_clock_version: 2;
  time_unobserved_ms: number;
  time_delayed_ticks: number;
  time_state: 'foreground' | 'background' | 'unmounted';
  time_foreground_observed: boolean;
  time_background_observed: boolean;
  time_unmounted_observed: boolean;
  time_gap: boolean;
};
export function sportsbookTimeState(s: TimeState): TimeObservation['time_state'] {
  return s.mounted === false ? 'unmounted' : s.visible ? 'foreground' : 'background';
}
export function makeSportsbookTime(read: () => TimeState,
  send: (account: string, sportsbook_ms: number, widget_ms: number, observation: TimeObservation) => void,
  now: () => number = () => performance.now()) {
  let previous = read(), last = now(), book = 0, widget = 0;
  let unobserved = 0, delayed = 0;
  let foreground = false, background = false, unmounted = false, gap = false;
  const observe = (s: TimeState) => {
    if (!s.account) return;
    const state = sportsbookTimeState(s);
    foreground ||= state === 'foreground'; background ||= state === 'background'; unmounted ||= state === 'unmounted';
  };
  observe(previous);
  const flush = () => {
    const account = previous.account, observed = foreground || background || unmounted;
    const bookMs = Math.round(book), widgetMs = Math.round(widget);
    const observation: TimeObservation = {time_tracking_version: 1, time_clock_version: 2,
      time_unobserved_ms: Math.round(unobserved), time_delayed_ticks: delayed,
      time_state: foreground ? 'foreground' : background ? 'background' : 'unmounted',
      time_foreground_observed: foreground, time_background_observed: background,
      time_unmounted_observed: unmounted, time_gap: gap};
    // Clear before callbacks, which can synchronously request another flush.
    book = 0; widget = 0; unobserved = delayed = 0; foreground = background = unmounted = gap = false;
    if (account && observed) send(account, bookMs, widgetMs, observation);
  };
  let ticking = false;
  const tick = () => {
    if (ticking) return;
    ticking = true;
    try {
      const current = read(), at = now(), delta = at - last;
      const accountChanged = current.account !== previous.account;
      const changed = accountChanged || sportsbookTimeState(current) !== sportsbookTimeState(previous);
      // Visibility/focus/freeze events delimit foreground segments. Delayed
      // callbacks within the existing reporting cadence are elapsed time, not
      // a missing sample. Longer unobserved intervals remain explicit gaps.
      if (!accountChanged && previous.account && sportsbookTimeState(previous) === 'foreground') {
        observe(previous);
        if (delta >= 0 && delta <= SPORTSBOOK_TIME_FLUSH_MS) {
          book += delta;
          if (previous.widget && current.widget) widget += delta;
          if (delta > SPORTSBOOK_TIME_SAMPLE_MS * 2) delayed++;
        } else { gap = true; unobserved += Math.max(0, delta); }
      }
      if (changed) flush();
      previous = current; last = at; observe(current);
      // Bound every payload by the existing flush cadence, even when the
      // regular flush callback is delayed behind the sample callback.
      if (changed || book >= SPORTSBOOK_TIME_FLUSH_MS) flush();
    } finally { ticking = false; }
  };
  return { tick, flush, stop: () => { tick(); flush(); } };
}
